/**
 * AppSettingsPage (앱 설정 페이지)
 *
 * 애플리케이션 일반 설정을 관리하는 페이지
 *
 * 책임:
 * - 다크 모드 토글
 * - 테마 색상 프리셋 선택
 *
 * 참고: 개발자 전용 설정은 DeveloperSettingsPage로 분리됨
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThemeStore, THEME_PRESETS, type ThemePreset } from '@/core/theme/themeStore'
import type { TransactionDisplayMode } from '@/domains/transaction/types/TransactionDisplayMode'
import { DEFAULT_DISPLAY_MODE } from '@/domains/transaction/types/TransactionDisplayMode'
import { STORAGE_KEYS } from '@/core/config/constants'

export function AppSettingsPage() {
  const navigate = useNavigate()
  const { mode, preset, setMode, setPreset } = useThemeStore()

  // 거래 내역 표시 방식 (localStorage에서 초기값 로드)
  const [displayMode, setDisplayModeState] = useState<TransactionDisplayMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TRANSACTION_DISPLAY_MODE)
    return (saved === 'compact' || saved === 'full') ? saved : DEFAULT_DISPLAY_MODE
  })

  /**
   * 거래 내역 표시 방식 변경 핸들러
   * localStorage에 저장하여 영구 보존
   */
  const setDisplayMode = (mode: TransactionDisplayMode) => {
    setDisplayModeState(mode)
    localStorage.setItem(STORAGE_KEYS.TRANSACTION_DISPLAY_MODE, mode)
  }

  /**
   * 다크 모드 토글 핸들러
   */
  const handleDarkModeToggle = () => {
    setMode(mode === 'light' ? 'dark' : 'light')
  }

  /**
   * 테마 프리셋 변경 핸들러
   */
  const handlePresetChange = (newPreset: ThemePreset) => {
    setPreset(newPreset)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="px-4 py-6 max-w-4xl mx-auto">
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 mb-2 flex items-center gap-1"
          >
            ← 뒤로 가기
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">앱 설정</h1>
        </div>

        {/* 일반 앱 설정 */}
        <div className="space-y-4">
          {/* 다크 모드 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">다크 모드</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {mode === 'dark' ? '다크' : '라이트'} 모드 활성화
                </p>
              </div>
              <button
                onClick={handleDarkModeToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  mode === 'dark' ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-label={`다크 모드 ${mode === 'dark' ? '비활성화' : '활성화'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    mode === 'dark' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* 테마 색상 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">테마 색상</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                앱의 기본 색상을 선택하세요 (변경 즉시 반영)
              </p>
              <div className="grid grid-cols-3 gap-3">
                {(Object.keys(THEME_PRESETS) as ThemePreset[]).map((presetName) => (
                  <button
                    key={presetName}
                    onClick={() => handlePresetChange(presetName)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      preset === presetName
                        ? 'border-primary-600 ring-2 ring-primary-100 dark:ring-primary-900'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div
                      className="w-full h-12 rounded mb-2"
                      style={{
                        backgroundColor: THEME_PRESETS[presetName].primary[500],
                      }}
                    />
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {presetName === 'blue' && '블루'}
                      {presetName === 'purple' && '퍼플'}
                      {presetName === 'green' && '그린'}
                    </p>
                    {preset === presetName && (
                      <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                        ✓ 적용 중
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 거래 내역 표시 방식 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                거래 내역 표시 방식
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                홈 화면 거래 목록의 표시 방식을 선택하세요
              </p>

              <div className="space-y-3">
                {/* 1줄보기 (간결) */}
                <label
                  className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    displayMode === 'compact'
                      ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="displayMode"
                    value="compact"
                    checked={displayMode === 'compact'}
                    onChange={(e) => setDisplayMode(e.target.value as TransactionDisplayMode)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        1줄보기 (간결)
                      </span>
                      {displayMode === 'compact' && (
                        <span className="text-xs text-primary-600 dark:text-primary-400">✓ 적용 중</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      아이콘, 메모, 금액만 표시하여 한 화면에 더 많은 거래를 볼 수 있습니다
                    </p>
                  </div>
                </label>

                {/* 2줄보기 (상세) */}
                <label
                  className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    displayMode === 'full'
                      ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="displayMode"
                    value="full"
                    checked={displayMode === 'full'}
                    onChange={(e) => setDisplayMode(e.target.value as TransactionDisplayMode)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        2줄보기 (상세) - 기본
                      </span>
                      {displayMode === 'full' && (
                        <span className="text-xs text-primary-600 dark:text-primary-400">✓ 적용 중</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      시간, 결제수단, 지출자 정보까지 모두 표시합니다
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
